import { debounce, get, isArray, some } from "lodash-es";
import objectHash from "object-hash";
import { useCallback, useEffect, useState } from "react";

import { Box, Stack } from "@mui/material";

import { toggles } from "../../../constants/policy-toggles";
import {
  getPolicyFormProperty,
  setPolicyFormProperty,
} from "../../../slices/policies";
import { useDispatch, useSelector } from "../../../store";
import { PplRule, PplRuleBody } from "../../../store/pomeriumApi";
import { hasToggleCriterion } from "../../../utils/ppl";
import AddAllowDeny from "./AddAllowDeny";
import AllowDenyBlock from "./AllowDenyBlock";
import ConditionBlock from "./ConditionBlock";
import Toggles from "./Toggles";

type PPLBuilderProps = {
  readonly: boolean;
  inDialog?: boolean;
};

export default function PPLBuilder({ readonly, inDialog }: PPLBuilderProps) {
  const ppl = useSelector(getPolicyFormProperty("ppl", [])) as
    | PplRule
    | PplRule[];
  const [pplArr, setPplArr] = useState<PplRule[]>([]);
  const dispatch = useDispatch();

  useEffect(() => {
    if (isArray(ppl)) {
      setPplArr(ppl);
    } else if (Object.keys(ppl).length) {
      setPplArr([ppl]);
    } else {
      setPplArr([]);
    }
  }, [objectHash(ppl)]);

  const delayedOnSave = useCallback(
    debounce((val: PplRule[]) => {
      dispatch(
        setPolicyFormProperty({
          path: "ppl",
          value: val,
        }),
      );
    }, 300),
    [dispatch, setPolicyFormProperty],
  );

  function savePPL(value: PplRule[]): void {
    setPplArr(value || []);
    delayedOnSave(value || []);
  }

  return (
    <>
      <Stack gap={2}>
        <AddAllowDeny readonly={readonly} pplArr={pplArr} savePPL={savePPL} />
        <Toggles readonly={readonly} pplArr={pplArr} savePPL={savePPL} />
        <Box
          sx={
            inDialog
              ? {
                  p: "1px",
                  overflowY: "auto",
                  height:
                    "clamp(60px, calc(100vh - 540px), calc(100vh - 540px))",
                }
              : {}
          }
        >
          <Stack gap={2}>
            {pplArr?.map((block, blockIDX) => {
              const allowDeny = Object.keys(block)[0] as "allow" | "deny";
              const conditions = block[allowDeny];

              if (
                some(Object.keys(toggles), (criterionName: string) =>
                  hasToggleCriterion(block, criterionName),
                )
              ) {
                return null;
              }

              return (
                <AllowDenyBlock
                  key={"allowDenyBlock" + blockIDX}
                  readonly={readonly}
                  allowDeny={allowDeny}
                  blockIDX={blockIDX}
                  savePPL={savePPL}
                  pplArr={pplArr}
                  currentConditions={Object.keys(
                    conditions || ({} as PplRuleBody),
                  )}
                >
                  {Object.keys(conditions || ({} as PplRuleBody)).map(
                    (condition) => {
                      return (
                        <ConditionBlock
                          key={blockIDX + "conditionBlock" + condition}
                          readonly={readonly}
                          pplArr={pplArr}
                          savePPL={savePPL}
                          blockIDX={blockIDX}
                          allowDeny={allowDeny}
                          condition={condition as "and" | "or" | "not" | "nor"}
                          rules={get(conditions, condition, [])}
                        />
                      );
                    },
                  )}
                </AllowDenyBlock>
              );
            })}
          </Stack>
        </Box>
      </Stack>
    </>
  );
}
